import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../utils/api";
import {
  shouldShowAnnouncementPopup,
  recordAnnouncementDismissed,
  recordAnnouncementShown,
  selectNextPopup,
  setLastShownPopupId,
} from "../utils/announcementPopupStorage";
import "./AnnouncementPopup.css";

function AnnouncementGlyph() {
  /* Megaphone — reads as announcement, not volume/speaker (Lucide megaphone paths) */
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

/**
 * After login only: at most one announcement per 24h (localStorage).
 * Next eligible view picks a random active popup (GET /popups/active), avoiding the previous id when possible.
 */
export default function AnnouncementPopup() {
  const location = useLocation();
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const onPrivateRoute =
    location.pathname !== "/login" && token && token !== "undefined";

  const dismiss = useCallback(() => {
    recordAnnouncementDismissed();
    setVisible(false);
    setPopup(null);
  }, []);

  useEffect(() => {
    if (!onPrivateRoute) {
      setPopup(null);
      setVisible(false);
      return;
    }

    let cancelled = false;

    async function load() {
      if (!shouldShowAnnouncementPopup()) {
        if (process.env.NODE_ENV === "development") {
          console.debug(
            "[AnnouncementPopup] Skipped: 24h cooldown (clear localStorage keys enviz_popup_* to retest)",
          );
        }
        return;
      }

      try {
        const { data } = await api.get("/popups/active", { timeout: 15000 });
        const list = Array.isArray(data) ? data : data?.popups || [];
        if (!list.length) {
          if (process.env.NODE_ENV === "development") {
            console.debug(
              "[AnnouncementPopup] API returned no active popups (empty list). Check DB is_active and background_image on same DB as enviz_api.",
            );
          }
          if (cancelled) return;
          return;
        }
        if (cancelled) return;

        const chosen = selectNextPopup(list);
        if (!chosen || cancelled) return;

        recordAnnouncementShown();
        setLastShownPopupId(chosen.id);
        setPopup(chosen);
        setVisible(true);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.debug("[AnnouncementPopup] No popup or API error:", e?.message);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // Intentionally omit location.pathname: tab changes should not fetch again in one session.
  }, [onPrivateRoute]);

  useEffect(() => {
    if (!visible) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  if (!visible || !popup) return null;

  const bg =
    popup.background_image ||
    popup.backgroundImage ||
    (popup.background_image_url ? popup.background_image_url : "");

  const i1 = popup.icon1_image ?? popup.icon1Image;
  const i2 = popup.icon2_image ?? popup.icon2Image;
  const i3 = popup.icon3_image ?? popup.icon3Image;
  const l1 = popup.icon1_link ?? popup.icon1Link ?? "";
  const l2 = popup.icon2_link ?? popup.icon2Link ?? "";
  const l3 = popup.icon3_link ?? popup.icon3Link ?? "";

  return (
    <div
      className="announcement-overlay"
      role="presentation"
      onClick={dismiss}
    >
      <div
        className="announcement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-heading"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="announcement-bg"
          style={
            bg
              ? { backgroundImage: `url(${bg})` }
              : undefined
          }
        >
          <div className="announcement-scrim" />
          <div className="announcement-body">
            <div className="announcement-topbar">
              <span className="announcement-announce-icon">
                <AnnouncementGlyph />
              </span>
              <button
                type="button"
                className="announcement-close-inner"
                aria-label="Close announcement"
                onClick={dismiss}
              >
                ×
              </button>
            </div>
            <div className="announcement-main">
              <h2 id="announcement-heading" className="announcement-heading">
                {popup.heading}
              </h2>
              <p className="announcement-desc">{popup.description}</p>
            </div>
            <div className="announcement-footer">
              <div className="announcement-divider" aria-hidden />
              <div className="announcement-icons">
                {[
                  { img: i1, href: l1 },
                  { img: i2, href: l2 },
                  { img: i3, href: l3 },
                ].map((slot, idx) =>
                  slot.img ? (
                    <a
                      key={idx}
                      className="announcement-icon-link"
                      href={slot.href || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!slot.href) e.preventDefault();
                      }}
                    >
                      {/* eslint-disable-next-line jsx-a11y/alt-text */}
                      <img src={slot.img} alt="" />
                    </a>
                  ) : null,
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
